<template>
  <div class="table-wrapper">
    <transition-group
      tag="table"
      name="slide-from-top"
      class="table"
    >
      <tr
        v-for="(row, rowIndex) in currentRows"
        :key="`table-${_uid}-row-${rowIndex}`"
        class="table__row"
      >
        <td
          v-for="(col, colIndex) in getCols(row)"
          :key="`table-${_uid}-col-${rowIndex}-${colIndex}`"
          :class="['table__col', { 'table__col_custom-style': col.custom_style }]"
        >
          <div class="table__col-text">
            {{ col.text }}
            <BaseHint v-if="col.hint" :hintText="col.hint" />
            <BaseSelect
              v-if="col.list"
              scrollbar="none"
              :colIndex="rowIndex"
              :items="Object.values(col.list).map(item => ({ text: item.label, value: item.id }))"
              class="table__col-text-select"
              @input="$emit('oilChange', $event, rowIndex)"
            />
          </div>
          <div
            v-if="col.subtext"
            class="table__col-subtext"
          >
            ({{ col.subtext }})
          </div>
        </td>
      </tr>
    </transition-group>

    <div
      v-if="rows.length > 4"
      class="table__expand-button-container"
    >
      <BaseButton
        class="table__expand-button"
        type="secondary"
        @click="toggleExpand"
      >
        {{ currentExpandButtonText }}
      </BaseButton>
    </div>
  </div>
</template>

<script>
  import { isObject } from 'lodash'

  export default {
    name: 'BaseTable',
    props: {
      expandText: {
        type: Object,
        default: null,
        validator (expandText) {
          return 'showAll' in expandText &&
            'showMain' in expandText
        }
      },
      rows: {
        type: Array,
        required: true,
        validator (rows) {
          return rows.every(row => isObject(row))
        }
      }
    },
    data () {
      return {
        isExpanded: false
      }
    },
    computed: {
      currentRows () {
        return this.isExpanded
          ? this.rows
          : this.rows.filter((row, index) => index < 4)
      },
      currentExpandButtonText () {
        return this.isExpanded
          ? this.expandText.showMain
          : this.expandText.showAll
      }
    },
    methods: {
      getCols (row) {
        return Object.keys(row).map(colId => row[colId])
      },
      toggleExpand () {
        this.isExpanded = !this.isExpanded
      }
    }
  }
</script>

<style lang="scss">
  .table {
    &-wrapper {
      padding: 30px 0;
      background: $c-white;

      @include mobile {
        padding: 0;
        padding-bottom: 25px;
        overflow-x: scroll;
      }
    }

    width: 100%;

    &__col {
      padding: 15px 0;
      padding-right: 45px;
      line-height: 1.5;
      text-align: center;
      vertical-align: middle;

      &_custom-style {
        font-size: 12px;
      }

      @include mobile {
        padding: 10px 0;
        padding-right: 25px;
      }

      &:first-child {
        padding-left: 40px;
        text-align: left;

        @include mobile {
          padding-left: 20px;
        }
      }

      &:last-child {
        padding-right: 40px;

        @include mobile {
          padding-right: 20px;
        }
      }

      &-text {
        position: relative;
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        color: $c-gray-dark;

        &-select {
          min-width: auto !important;
          max-width: 100% !important;
          padding: 0 !important;
          margin-left: 15px;

          .select__text {
            @include mobile {
              white-space: nowrap;
            }
          }

          .select__arrow {
            right: 10px;
          }

          .select__dropdown {
            top: 100% !important;
            width: max-content;

            @include no-scrollbar;
          }
        }

        @include mobile {
          font-size: 14px;
        }
      }

      &-subtext {
        max-width: 290px;
        font-size: 12px;
        color: $c-gray-dark;
        opacity: 0.7;

        @include mobile {
          font-size: 10px;
        }
      }
    }

    &__row {
      &:nth-child(even) {
        background: $c-gray-extra-light;
      }

      &:first-child {
        .table__col-text {
          font-size: 18px;
          font-weight: 900;
          color: $c-black;

          @include mobile {
            font-size: 16px;
          }
        }
      }
    }

    &__expand-button-container {
      display: flex;
      justify-content: center;
      margin-top: 25px;
    }

    .table__col-text .base-hint {
      height: 28px;
      margin-left: 5px;
    }
  }
</style>
